/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useRef } from 'react';
import styles from '../../../styles/components/pageComponents/program/ProgramGoalsModal.module.css';
import { useGoalType } from '../../../hooks/useGoalType';
import SuccessContent from '../../controlVariations/ModularSuccessModal';

interface ProgramGoalFormData {
	goalType: string;
	goalTypeId: string;
	description: string;
	targetValue: string;
	unit: string;
}

interface ProgramGoalsModalProps {
	isOpen: boolean;
	onClose: () => void;

	userId: string;
	programId?: string;
	// Callback hooks ready for your API integration
	onSubmitGoal: (data: ProgramGoalFormData) => Promise<void> | void;
	onAddCustomGoalType: (newType: string) => Promise<void> | void;
	initialGoalTypes?: string[];
	initialUnits?: string[];
	usedGoalTypeIds?: string[];
}

export const ProgramGoalsModal: React.FC<ProgramGoalsModalProps> = ({
	isOpen,
	onClose,
	userId,
	programId,
	onSubmitGoal,
	onAddCustomGoalType,
	initialGoalTypes = [
		'Coaching Hours',
		'Recordings with Transcript',
		'Attendance',
		'Assignments',
	],
	initialUnits = ['Number', 'Hours', 'Percentage'],
	usedGoalTypeIds = [],
}) => {
	// Main Form States
	// const [goalTypes, setGoalTypes] = useState<string[]>(initialGoalTypes);
	// const [selectedGoalType, setSelectedGoalType] = useState('');
	const [selectedGoalType, setSelectedGoalType] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [description, setDescription] = useState('');
	const [targetValue, setTargetValue] = useState('');
	const [selectedUnit, setSelectedUnit] = useState('Number');

	// Dropdown UI Open States
	const [isGoalTypeDropdownOpen, setIsGoalTypeDropdownOpen] = useState(false);
	const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false);

	// Sub-Modal Overlay state for "New Goal Type"
	const [showNewGoalTypeModal, setShowNewGoalTypeModal] = useState(false);
	const [newGoalTypeName, setNewGoalTypeName] = useState('');

	// Inline Validation Error States
	const [errors, setErrors] = useState<{
		goalType?: string;
		description?: string;
		newGoalType?: string;
		targetValue?: string;
	}>({});

	const [showSuccessPopup, setShowSuccessPopup] = useState(false);

	/**
	 * Goal Type Hook
	 * Fetches goal types from backend
	 */
	const { goalTypes, loading, creating, createGoalType } = useGoalType();

	// Set for O(1) lookups while rendering the option list.
	const usedGoalTypeIdSet = new Set(usedGoalTypeIds);

	// Refs for closing dropdowns when clicking outside
	const goalTypeDropdownRef = useRef<HTMLDivElement>(null);
	const unitDropdownRef = useRef<HTMLDivElement>(null);

	// Handle outside click to close dropdown menus
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				goalTypeDropdownRef.current &&
				!goalTypeDropdownRef.current.contains(event.target as Node)
			) {
				setIsGoalTypeDropdownOpen(false);
			}
			if (
				unitDropdownRef.current &&
				!unitDropdownRef.current.contains(event.target as Node)
			) {
				setIsUnitDropdownOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () =>
			document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	if (!isOpen) return null;

	// Main Form Submission Handler
	const handleAddGoalSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const newErrors: typeof errors = {};

		if (!selectedGoalType) {
			setErrors(prev => ({
				...prev,
				goalType: 'Program Goal Type is required',
			}));

			return;
		}

		if (usedGoalTypeIdSet.has(selectedGoalType.id)) {
			setErrors(prev => ({
				...prev,
				goalType: `"${selectedGoalType.name}" is already used by another goal in this program. Pick a different goal type.`,
			}));

			return;
		}

		if (!description.trim()) {
			newErrors.description = 'Program Goal Description is required';
		}

		if (targetValue && isNaN(Number(targetValue))) {
			setErrors(prev => ({
				...prev,
				targetValue: 'Target Value must be a number',
			}));

			return;
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		try {
			await onSubmitGoal({
				goalType: selectedGoalType.name,
				goalTypeId: selectedGoalType.id,
				description,
				targetValue,
				unit: selectedUnit,
			});

			resetMainForm();

			onClose();
		} catch (error: any) {
			console.error('Create Goal Error:', error);

			// Keep the modal open and explain the rejection next to the field.
			setErrors(prev => ({
				...prev,
				goalType:
					error?.message || 'Could not create this goal. Please try again.',
			}));
		}
	};
	// Sub-Form Submission Handler (Add New Goal Type)
	/**
	 * Add New Goal Type Handler
	 * Integrates backend API using useGoalType hook
	 */
	/**
	 * Add New Goal Type Handler
	 */
	// const handleAddNewGoalTypeSubmit = async (e: React.FormEvent) => {
	// 	e.preventDefault();

	// 	/**
	// 	 * Validation
	// 	 */
	// 	if (!newGoalTypeName.trim()) {
	// 		setErrors(prev => ({
	// 			...prev,
	// 			newGoalType: 'Goal Type Name cannot be empty',
	// 		}));

	// 		return;
	// 	}

	// 	try {
	// 		/**
	// 		 * Backend API Call
	// 		 */
	// 		const createdGoalType = await createGoalType({
	// 			name: newGoalTypeName.trim(),
	// 		});

	// 		if (!createdGoalType) {
	// 			return;
	// 		}

	// 		/**
	// 		 * Auto-select newly created goal type
	// 		 */
	// 		setSelectedGoalType({
	// 			id: createdGoalType.id,
	// 			name: createdGoalType.name,
	// 		});

	// 		/**
	// 		 * Optional parent callback
	// 		 */
	// 		if (onAddCustomGoalType) {
	// 			await onAddCustomGoalType(newGoalTypeName.trim());
	// 		}

	// 		/**
	// 		 * Cleanup
	// 		 */
	// 		setNewGoalTypeName('');

	// 		setErrors(prev => ({
	// 			...prev,
	// 			newGoalType: undefined,
	// 		}));

	// 		/**
	// 		 * Close sub modal
	// 		 */
	// 		setShowNewGoalTypeModal(false);

	// 		setShowSuccessPopup(true);

	// 		setTimeout(() => {
	// 			setShowSuccessPopup(false);
	// 		}, 3000);
	// 	} catch (error) {
	// 		console.error('Error adding goal type:', error);

	// 		setErrors(prev => ({
	// 			...prev,
	// 			newGoalType: 'Failed to create goal type',
	// 		}));
	// 	}
	// };
	const handleAddNewGoalTypeSubmit = async (e: React.FormEvent) => {
	e.preventDefault();

	if (!newGoalTypeName.trim()) {
		setErrors(prev => ({
			...prev,
			newGoalType: 'Goal Type Name cannot be empty',
		}));
		return;
	}

	try {
		const createdGoalType = await createGoalType({
			name: newGoalTypeName.trim(),
		});

		// If API returns success: false
		if (!createdGoalType) {
			setErrors(prev => ({
				...prev,
				newGoalType: 'A goal type with this name already exists',
			}));
			return;
		}

		// Select newly created goal type
		setSelectedGoalType({
			id: createdGoalType.id,
			name: createdGoalType.name,
		});

		if (onAddCustomGoalType) {
			await onAddCustomGoalType(newGoalTypeName.trim());
		}

		setNewGoalTypeName('');

		setErrors(prev => ({
			...prev,
			newGoalType: undefined,
		}));

		setShowNewGoalTypeModal(false);

		setShowSuccessPopup(true);

		setTimeout(() => {
			setShowSuccessPopup(false);
		}, 3000);
	} catch (error: any) {
		console.error('Error adding goal type:', error);

		// Get backend error message
		const message =
			error?.response?.data?.message ||
			error?.message ||
			'Failed to create goal type';

		setErrors(prev => ({
			...prev,
			newGoalType: message,
		}));
	}
};

	const resetMainForm = () => {
		setSelectedGoalType(null);
		setDescription('');
		setTargetValue('');
		setSelectedUnit('Number');
		setErrors({});
	};

	const handleTargetValueChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const value = e.target.value;

		/**
		 * Allow empty value
		 */
		if (value === '') {
			setTargetValue('');

			setErrors(prev => ({
				...prev,
				targetValue: undefined,
			}));

			return;
		}

		/**
		 * Numbers only
		 */
		if (/^\d+$/.test(value)) {
			setTargetValue(value);

			setErrors(prev => ({
				...prev,
				targetValue: undefined,
			}));

			return;
		}

		/**
		 * Invalid character
		 */
		setErrors(prev => ({
			...prev,
			targetValue: 'Only numeric values are allowed',
		}));
	};

	return (
		<div className={styles.modalOverlay}>
			{/* Container wrapper for the Primary Modal */}
			<div className={styles.modalContainer}>
				<div className={styles.modalHeader}>
					<h2>Add Program Goals</h2>
					<button
						className={styles.closeButton}
						onClick={onClose}
						aria-label="Close modal"
					>
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
						>
							<line x1="18" y1="6" x2="6" y2="18"></line>
							<line x1="6" y1="6" x2="18" y2="18"></line>
						</svg>
					</button>
				</div>

				<form
					onSubmit={handleAddGoalSubmit}
					className={styles.modalBody}
				>
					{/* Program Goal Type Block */}
					<div className={styles.formGroup}>
						<div className={styles.labelRow}>
							<label className={styles.fieldLabel}>
								Program Goal Type
								<span className={styles.asterisk}>*</span>
							</label>
							<button
								type="button"
								className={styles.addNewTypeBtn}
								onClick={() => setShowNewGoalTypeModal(true)}
							>
								Add New Goal Type +
							</button>
						</div>

						<div
							className={styles.customSelectWrapper}
							ref={goalTypeDropdownRef}
						>
							<div
								className={`${styles.customSelectTrigger} ${errors.goalType ? styles.inputErrorBorder : ''} ${!selectedGoalType ? styles.placeholderText : ''}`}
								onClick={() =>
									setIsGoalTypeDropdownOpen(
										!isGoalTypeDropdownOpen
									)
								}
							>
								{selectedGoalType?.name || 'Coaching Hours'}
								<span
									className={`${styles.chevron} ${isGoalTypeDropdownOpen ? styles.chevronOpen : ''}`}
								></span>
							</div>
							{isGoalTypeDropdownOpen && (
								<ul className={styles.customOptionsList}>
									{goalTypes.map(type => {
										const isUsed = usedGoalTypeIdSet.has(
											type.id
										);

										return (
											<li
												key={type.id}
												aria-disabled={isUsed}
												title={
													isUsed
														? 'Already used by another goal in this program'
														: undefined
												}
												className={`
                                                 ${styles.customOption}
                                                 ${isUsed ? styles.disabledOption : ''}
                                                 ${selectedGoalType?.id ===
													type.id
													? styles.selectedOption
													: ''
												}
                                                `}
												onClick={() => {
													if (isUsed) {
														return;
													}

													setSelectedGoalType({
														id: type.id,
														name: type.name,
													});
													setIsGoalTypeDropdownOpen(
														false
													);

													setErrors(prev => ({
														...prev,
														goalType: undefined,
													}));
												}}
											>
												{type.name}
												{isUsed && (
													<span
														className={
															styles.optionBadge
														}
													>
														Already added
													</span>
												)}
											</li>
										);
									})}
								</ul>
							)}
						</div>
						{errors.goalType && (
							<p className={styles.errorText}>
								{errors.goalType}
							</p>
						)}
					</div>

					{/* Program Goal Description Block */}
					<div className={styles.formGroup}>
						<label className={styles.fieldLabel}>
							Program Goal Description
							<span className={styles.asterisk}>*</span>
						</label>
						<textarea
							className={`${styles.textareaField} ${errors.description ? styles.inputErrorBorder : ''}`}
							placeholder="Complete a minimum of 25 hours of dedicated coaching sessions."
							value={description}
							onChange={e => {
								setDescription(e.target.value);
								if (e.target.value.trim()) {
									setErrors(prev => ({
										...prev,
										description: undefined,
									}));
								}
							}}
						/>
						{errors.description && (
							<p className={styles.errorText}>
								{errors.description}
							</p>
						)}
					</div>

					{/* Responsive Row split layout for Target Value and Unit */}
					<div className={styles.formResponsiveRow}>
						<div className={styles.formGroupFlex}>
							<label className={styles.fieldLabel}>
								Target Value
							</label>
							<input
								type="text"
								className={styles.inputField}
								placeholder="25"
								value={targetValue}
								onPaste={e => {
									const pastedText =
										e.clipboardData.getData('text');

									if (!/^\d+$/.test(pastedText)) {
										e.preventDefault();

										setErrors(prev => ({
											...prev,
											targetValue:
												'Only numeric values are allowed',
										}));
									}
								}}
								onChange={e => {
									setTargetValue(e.target.value);

									setErrors(prev => ({
										...prev,
										targetValue: undefined,
									}));
								}}
							/>

							{errors.targetValue && (
								<p className={styles.errorText}>
									{errors.targetValue}
								</p>
							)}
						</div>

						<div className={styles.formGroupFlex}>
							<label className={styles.fieldLabel}>Unit</label>
							<div
								className={styles.customSelectWrapper}
								ref={unitDropdownRef}
							>
								<div
									className={styles.customSelectTrigger}
									onClick={() =>
										setIsUnitDropdownOpen(
											!isUnitDropdownOpen
										)
									}
								>
									{selectedUnit}
									<span
										className={`${styles.chevron} ${isUnitDropdownOpen ? styles.chevronOpen : ''}`}
									></span>
								</div>
								{isUnitDropdownOpen && (
									<ul className={styles.customOptionsList}>
										{initialUnits.map(unitItem => (
											<li
												key={unitItem}
												className={`${styles.customOption} ${selectedUnit === unitItem ? styles.selectedOption : ''}`}
												onClick={() => {
													setSelectedUnit(unitItem);
													setIsUnitDropdownOpen(
														false
													);
												}}
											>
												{unitItem}
											</li>
										))}
									</ul>
								)}
							</div>
						</div>
					</div>

					<div className={styles.formFooter}>
						<button
							type="submit"
							className={styles.primaryActionButton}
							disabled={creating}
						>
							{creating ? 'Adding...' : 'Add Goal'}
						</button>
					</div>
				</form>
			</div>

			{/* Secondary Nestled Dialog: "New Goal Type" */}
			{showNewGoalTypeModal && (
				<div className={styles.subModalOverlay}>
					<div className={styles.subModalContainer}>
						<div className={styles.modalHeader}>
							<h3>New Goal Type</h3>
							<button
								type="button"
								className={styles.closeButton}
								onClick={() => {
									setShowNewGoalTypeModal(false);
									setErrors(prev => ({
										...prev,
										newGoalType: undefined,
									}));
								}}
							>
								<svg
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
								>
									<line x1="18" y1="6" x2="6" y2="18"></line>
									<line x1="6" y1="6" x2="18" y2="18"></line>
								</svg>
							</button>
						</div>
						<form
							onSubmit={handleAddNewGoalTypeSubmit}
							className={styles.modalBody}
						>
							<div className={styles.formGroup}>
								<label className={styles.fieldLabel}>
									Goal Type Name
								</label>
								<input
									type="text"
									className={`${styles.inputField} ${errors.newGoalType ? styles.inputErrorBorder : ''}`}
									placeholder="Add Goal Type Name"
									value={newGoalTypeName}
									onChange={e => {
										setNewGoalTypeName(e.target.value);
										if (e.target.value.trim()) {
											setErrors(prev => ({
												...prev,
												newGoalType: undefined,
											}));
										}
									}}
								/>
								{errors.newGoalType && (
									<p className={styles.errorText}>
										{errors.newGoalType}
									</p>
								)}
							</div>
							<div className={styles.formFooter}>
								<button
									type="submit"
									className={styles.primaryActionButton}
									disabled={creating}
								>
									{creating ? 'Adding...' : 'Add Goal Type'}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{showSuccessPopup && (
				<SuccessContent
					title="Goal Type Added"
					subtitle="New goal type created successfully."
				/>
			)}
		</div>
	);
};
